/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import { useEffect, useRef } from 'react'
import { openCrossTabWrites } from 'app/lib/crossTabWrites'
import { CustomPageType } from 'app/types/entities'

const CUSTOM_PAGE_CREATED_EVENT = `customPageCreated`
const CUSTOM_PAGE_DELETED_EVENT = `customPageDeleted`

/**
 * Module-level store for the most recently created custom page.
 * Survives route transitions so the destination route can read it on mount.
 */
let _lastCreatedPage: CustomPageType | null = null

/** Store a just-created page so the destination route can read it on mount. */
export function setLastCreatedPage(page: CustomPageType): void {
  _lastCreatedPage = page
}

/** Read and clear the last created page (one-shot consumption). */
export function consumeLastCreatedPage(): CustomPageType | null {
  const page = _lastCreatedPage
  _lastCreatedPage = null
  return page
}

/**
 * Pending page IDs — pages that were just created and may not yet appear in
 * ES search results. The merge logic retains only these IDs from previous
 * state. Once the server returns them, they are acknowledged and removed.
 * Entries auto-expire after 30 seconds to prevent unbounded growth.
 */
const _pendingPageIds = new Map<string, number>()

/** Max age in ms before a pending ID is evicted. */
const PENDING_MAX_AGE_MS = 30_000

/** Mark an ID as pending (just created, not yet indexed). */
export function addPendingPageId(id: string): void {
  _pendingPageIds.set(id, Date.now())
}

/** Remove an ID from pending (server acknowledged it). */
export function acknowledgePendingPageId(id: string): void {
  _pendingPageIds.delete(id)
}

/** Check whether an ID is still pending (not yet server-acknowledged). */
export function isPendingPageId(id: string): boolean {
  const ts = _pendingPageIds.get(id)
  if (ts == null) return false
  if (Date.now() - ts > PENDING_MAX_AGE_MS) {
    _pendingPageIds.delete(id)
    return false
  }
  return true
}

/**
 * Recently deleted page IDs. The customPages search index lags writes, so a refetch right
 * after a delete still returns the deleted page; readers drop these IDs from what they fetch.
 * Entries expire after the same 30 seconds as pending IDs.
 */
const _deletedPageIds = new Map<string, number>()

/** Check whether an ID was deleted recently enough that the index may still return it. */
export function isDeletedPageId(id: string): boolean {
  const ts = _deletedPageIds.get(id)
  if (ts == null) return false
  if (Date.now() - ts > PENDING_MAX_AGE_MS) {
    _deletedPageIds.delete(id)
    return false
  }
  return true
}

function recordDeletedPage(id: string) {
  _deletedPageIds.set(id, Date.now())
  _pendingPageIds.delete(id)
  window.dispatchEvent(new CustomEvent(CUSTOM_PAGE_DELETED_EVENT, { detail: id }))
}

const shareDeletedPage = openCrossTabWrites<CustomPageType>(`customPages`, (write) => {
  if (write.kind === `delete`) recordDeletedPage(write.id)
})

/**
 * Records a deleted page in this tab and in every other open tab, so each one drops its row
 * at once and keeps it out of the refetches that follow while the index catches up.
 */
export function markCustomPageDeleted(id: string): void {
  recordDeletedPage(id)
  shareDeletedPage({ kind: `delete`, id })
}

/**
 * Dispatches a custom event when a custom page is created
 * @param customPage The custom page that was created
 */
export function dispatchCustomPageCreated(customPage: CustomPageType) {
  window.dispatchEvent(new CustomEvent(CUSTOM_PAGE_CREATED_EVENT, { detail: customPage }))
}

/**
 * Adds an event listener for custom page creation events
 * @param callback Function to call when a custom page is created
 * @returns Function to remove the event listener
 */
export function onCustomPageCreated(callback: (customPage: CustomPageType) => void) {
  const handleEvent = (event: CustomEvent<CustomPageType>) => {
    callback(event.detail)
  }

  window.addEventListener(CUSTOM_PAGE_CREATED_EVENT, handleEvent as EventListener)

  return () => {
    window.removeEventListener(CUSTOM_PAGE_CREATED_EVENT, handleEvent as EventListener)
  }
}

/**
 * Custom hook to listen for custom page creation events.
 * Stores the callback in a ref to avoid teardown/reattach on every render.
 */
export function useCustomPageCreated(callback: (customPage: CustomPageType) => void) {
  const callbackRef = useRef(callback)
  callbackRef.current = callback

  useEffect(() => {
    const removeListener = onCustomPageCreated((page) => callbackRef.current(page))
    return removeListener
  }, [])
}

/** Calls back with the ID of each page deleted in this tab or another one. */
export function useCustomPageDeleted(callback: (id: string) => void) {
  const callbackRef = useRef(callback)
  callbackRef.current = callback

  useEffect(() => {
    const handleEvent = (event: Event) => callbackRef.current((event as CustomEvent<string>).detail)
    window.addEventListener(CUSTOM_PAGE_DELETED_EVENT, handleEvent)
    return () => window.removeEventListener(CUSTOM_PAGE_DELETED_EVENT, handleEvent)
  }, [])
}
